import { IBlackhatMonitoringFilters } from '../../model/types/BlackhatMonitoringFilters.interface'
import {
  selectBlackhatMonitoringFilters,
  setBlackhatMonitoringCountry,
  setBlackhatMonitoringDate,
  setBlackhatMonitoringDomain,
  setBlackhatMonitoringFilter,
  setBlackhatMonitoringTime,
} from '../../model/blackhatMonitoringSlice'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { useGetDecloakFilterQuery, useGetFilterCountryQuery } from '../../../../app/api'
import { useEffect } from 'react'
import { TimeIcon } from '../../../../shared/components/icons/filters/TimeIcon'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import styles from './BlackhatMonitoringFilters.module.scss'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { DateIcon } from '../../../../shared/components/icons/filters/DateIcon'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'

export const Filters = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<IBlackhatMonitoringFilters>) => {
  const { t } = useTranslation()
  const { domain, country, date, time } = useAppSelector(selectBlackhatMonitoringFilters)
  const { getValues, control } = useForm<IBlackhatMonitoringFilters>({
    mode: 'onChange',
    defaultValues: { domain, country, date, time, ...searchParams },
  })
  const dispatch = useAppDispatch()

  const { data, isLoading } = useGetDecloakFilterQuery({
    domain,
    countryId: country as unknown as number,
    date,
  })
  const countries = data?.data?.countries as any[] | undefined

  const { data: countryNames, isLoading: countryNamesLoading } = useGetFilterCountryQuery({})

  useEffect(() => {
    if (searchParams) dispatch(setBlackhatMonitoringFilter(searchParams))
  }, [])

  useEffect(() => {
    if (setSearchParams) {
      setSearchParams({ domain, country, date, time })
    }
  }, [domain, country, date, time])

  return (
    <>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              // useEffect(() => {
              //   dispatch(
              //     setBlackhatMonitoringCountry(
              //       countries?.includes(country)
              //         ? country
              //         : countries?.length
              //         ? countries[0]
              //         : searchParams
              //         ? searchParams?.country
              //         : '',
              //     ),
              //   )
              // }, [value])

              return (
                <BpSelect
                  {...fieldProps}
                  defaultValue={
                    data?.data?.domains?.includes(domain as string)
                      ? domain
                      : data?.data?.domains?.length
                        ? data?.data?.domains[0]
                        : ''
                  }
                  options={data?.data?.domains?.map((value: string) => ({
                    text: value,
                    value: value,
                  }))}
                  Icon={DomainIcon}
                  disabled={!data?.data?.domains?.length}
                  enableChipLoad={false}
                  fieldSize='lg'
                  loading={isLoading}
                  title={t('fields.domain')}
                  value={domain}
                  onChange={(e) => {
                    onChange(e)
                    dispatch(setBlackhatMonitoringDomain(getValues('domain') || domain || ''))
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='domain'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              useEffect(() => {
                if (countries?.includes(Number(getValues('country')))) return

                dispatch(
                  setBlackhatMonitoringCountry(
                    countries?.includes(country)
                      ? country
                      : countries?.length
                        ? countries[0]
                        : searchParams
                          ? searchParams?.country
                          : '',
                  ),
                )
              }, [countries])
              return (
                <BpSelect
                  {...fieldProps}
                  defaultValue={
                    countries?.includes(country) ? country : countries?.length ? countries[0] : ''
                  }
                  options={
                    countryNames &&
                    countries?.map((value) => {
                      const country = countryNames.find(
                        (country) => country.value === String(value),
                      )

                      return {
                        text: country?.text ?? '',
                        code: country?.code,
                        value: value,
                      }
                    })
                  }
                  Icon={CountryIcon}
                  disabled={!countries?.length}
                  enableChipLoad={false}
                  fieldSize='lg'
                  loading={isLoading || countryNamesLoading}
                  optionRender={renderCountryOption}
                  title={t('fields.country')}
                  value={country}
                  onChange={(e) => {
                    onChange(e)
                    dispatch(setBlackhatMonitoringCountry(getValues('country') || country || ''))
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              useEffect(() => {
                dispatch(
                  setBlackhatMonitoringDate(
                    data?.data?.dates?.includes(date as string)
                      ? date
                      : data?.data?.dates?.length
                        ? data?.data?.dates[0]
                        : searchParams
                          ? searchParams?.date
                          : '',
                  ),
                )
              }, [data?.data?.dates])

              return (
                <BpSelect
                  {...fieldProps}
                  defaultValue={
                    data?.data?.dates?.includes(date as string)
                      ? date
                      : data?.data?.dates?.length
                        ? data?.data?.dates[0]
                        : ''
                  }
                  options={data?.data?.dates?.map((value: string) => ({
                    text: value,
                    value: value,
                  }))}
                  Icon={DateIcon}
                  disabled={!data?.data?.dates?.length}
                  enableChipLoad={false}
                  fieldSize='lg'
                  loading={isLoading}
                  title={t('fields.date')}
                  value={date}
                  onChange={(e) => {
                    onChange(e)
                    dispatch(setBlackhatMonitoringDate(getValues('date') || date || ''))
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='date'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              useEffect(() => {
                setSearchParams && setSearchParams({ ...searchParams, time: value })
                dispatch(
                  setBlackhatMonitoringTime(
                    data?.data?.times?.some((value) => value.id === time)
                      ? time
                      : data?.data?.times?.length
                        ? data?.data?.times[0].id
                        : searchParams
                          ? searchParams?.time
                          : '',
                  ),
                )
              }, [data?.data?.times])

              return (
                <BpSelect
                  {...fieldProps}
                  defaultValue={
                    data?.data?.times?.some((value) => value.id === time)
                      ? time
                      : data?.data?.times?.length
                        ? data?.data?.times[0].id
                        : ''
                  }
                  options={data?.data?.times?.map((value) => ({
                    text: value.time ?? '',
                    value: value.id ?? '',
                  }))}
                  Icon={TimeIcon}
                  disabled={!data?.data?.times?.length}
                  enableChipLoad={false}
                  fieldSize='lg'
                  loading={isLoading}
                  title={t('fields.time')}
                  value={time}
                  onChange={(e) => {
                    onChange(e)
                    dispatch(setBlackhatMonitoringTime(getValues('time') || time || ''))
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='time'
          />
        </div>
      </form>
    </>
  )
}

export const BlackhatMonitoringFilters = withSearchParams<
  IWithSearchParamsProps<IBlackhatMonitoringFilters>,
  IBlackhatMonitoringFilters
>(Filters, selectBlackhatMonitoringFilters)
